/**
 * ICSC BOSS — Unified Design System Tokens
 * Single source of truth for all visual decisions.
 * Version: 1.0.0
 */

// ── Colour Palette ─────────────────────────────────────────────────────────────

export const colors = {
  // Brand / Primary
  primary:        "#1e40af",   // Deep Blue — CTAs, active states
  primaryHover:   "#1d4ed8",   // Hover variant
  primaryLight:   "#dbeafe",   // Tinted backgrounds
  primaryText:    "#1e40af",   // Text on light backgrounds

  // Secondary
  secondary:      "#64748b",   // Muted actions
  secondaryHover: "#475569",
  secondaryLight: "#f1f5f9",

  // Semantic
  success:        "#16a34a",
  successHover:   "#15803d",
  successLight:   "#dcfce7",

  warning:        "#d97706",
  warningHover:   "#b45309",
  warningLight:   "#fef9c3",

  danger:         "#dc2626",
  dangerHover:    "#b91c1c",
  dangerLight:    "#fee2e2",

  info:           "#0891b2",
  infoHover:      "#0e7490",
  infoLight:      "#e0f2fe",

  purple:         "#7c3aed",
  purpleHover:    "#6d28d9",
  purpleLight:    "#f3e8ff",

  // Text
  textPrimary:    "#0f172a",   // Headings, primary content
  textSecondary:  "#64748b",   // Subtext, labels
  textMuted:      "#94a3b8",   // Placeholders, hints
  textInverse:    "#ffffff",   // Text on dark backgrounds

  // Surface & Structure
  background:     "#f8fafc",   // App background
  backgroundAlt:  "#f1f5f9",   // Alternating rows, sub-sections
  surface:        "#ffffff",   // Cards, panels
  border:         "#e2e8f0",   // Dividers, outlines
  borderFocus:    "#1e40af",   // Focus rings

  // Sidebar
  sidebarBg:      "#0f172a",   // Dark navy sidebar
  sidebarText:    "#cbd5e1",   // Sidebar link text
  sidebarActive:  "#1e40af",   // Active link bg
  sidebarHover:   "#1e293b",   // Hover link bg
  sidebarBorder:  "#1e293b",   // Sidebar section divider
  sidebarHeading: "#475569",   // Section heading text

  // Topbar
  topbarBg:       "#ffffff",
  topbarBorder:   "#e2e8f0",
};

// ── Spacing Scale ──────────────────────────────────────────────────────────────

export const spacing = {
  xs:   "4px",
  sm:   "8px",
  md:   "16px",
  lg:   "24px",
  xl:   "32px",
  xxl:  "48px",
  xxxl: "64px",
};

// Numeric equivalents for use in calculations
export const spacingNum = {
  xs:   4,
  sm:   8,
  md:   16,
  lg:   24,
  xl:   32,
  xxl:  48,
  xxxl: 64,
};

// ── Typography ─────────────────────────────────────────────────────────────────

export const typography = {
  fontFamily: "-apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', 'Roboto', sans-serif",
  fontFamilyMono: "'JetBrains Mono', 'Fira Code', 'Courier New', monospace",

  fontSizes: {
    xs:   "11px",
    sm:   "12px",
    md:   "14px",
    lg:   "16px",
    xl:   "18px",
    xxl:  "22px",
    xxxl: "28px",
    display: "36px",
  },

  fontWeights: {
    regular:  400,
    medium:   500,
    semibold: 600,
    bold:     700,
  },

  lineHeights: {
    tight:   1.25,
    normal:  1.5,
    relaxed: 1.75,
  },
};

// ── Border Radius ──────────────────────────────────────────────────────────────

export const radius = {
  xs:   "4px",
  sm:   "6px",
  md:   "8px",
  lg:   "12px",
  xl:   "16px",
  pill: "9999px",
};

// ── Shadows ────────────────────────────────────────────────────────────────────

export const shadows = {
  xs:         "0 1px 2px rgba(0,0,0,0.05)",
  sm:         "0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)",
  cardShadow: "0 2px 8px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04)",
  md:         "0 4px 16px rgba(0,0,0,0.08)",
  modalShadow: "0 20px 60px rgba(0,0,0,0.2), 0 4px 20px rgba(0,0,0,0.1)",
  dropdownShadow: "0 8px 24px rgba(0,0,0,0.12)",
  focus:      "0 0 0 3px rgba(30,64,175,0.25)",
};

// ── Animation ──────────────────────────────────────────────────────────────────

export const animation = {
  fast:   "150ms ease",
  normal: "200ms ease",
  slow:   "300ms ease",

  // CSS keyframes as strings (for use in <style> tags or CSS-in-JS)
  fadeIn:  "fadeIn 200ms ease forwards",
  slideUp: "slideUp 250ms ease forwards",
  slideDown: "slideDown 200ms ease forwards",
};

// ── Z-Index Scale ──────────────────────────────────────────────────────────────

export const zIndex = {
  base:     0,
  raised:   10,
  dropdown: 100,
  sticky:   200,
  sidebar:  300,
  topbar:   400,
  modal:    500,
  toast:    600,
};

// ── Layout ─────────────────────────────────────────────────────────────────────

export const layout = {
  sidebarWidth:     "240px",
  sidebarCollapsed: "64px",
  topbarHeight:     "60px",
  contentMaxWidth:  "1280px",
  pageGutter:       "32px",
};

// ── Breakpoints ────────────────────────────────────────────────────────────────

export const breakpoints = {
  sm:  "640px",
  md:  "768px",
  lg:  "1024px",
  xl:  "1280px",
  xxl: "1536px",
};

// ── Status Colour Map ──────────────────────────────────────────────────────────
// Maps common status values to { bg, text, border, label } tokens

export const statusMap = {
  // Generic
  active:      { bg: "#dcfce7", text: "#166534", border: "#86efac", label: "Active" },
  inactive:    { bg: "#f1f5f9", text: "#64748b", border: "#cbd5e1", label: "Inactive" },
  suspended:   { bg: "#fef9c3", text: "#854d0e", border: "#fde047", label: "Suspended" },
  revoked:     { bg: "#fee2e2", text: "#991b1b", border: "#fca5a5", label: "Revoked" },
  lapsed:          { bg: "#fee2e2", text: "#991b1b", border: "#fca5a5", label: "Lapsed" },
  reapproval_due:  { bg: "#fff7ed", text: "#c2410c", border: "#fdba74", label: "Re-approval Due" },
  expired:     { bg: "#fff7ed", text: "#c2410c", border: "#fdba74", label: "Expired" },
  pending:     { bg: "#fef9c3", text: "#854d0e", border: "#fde047", label: "Pending" },
  approved:    { bg: "#dcfce7", text: "#166534", border: "#86efac", label: "Approved" },
  rejected:    { bg: "#fee2e2", text: "#991b1b", border: "#fca5a5", label: "Rejected" },
  draft:       { bg: "#f3f4f6", text: "#6b7280", border: "#d1d5db", label: "Draft" },

  // Accreditation
  granted:     { bg: "#dcfce7", text: "#166534", border: "#86efac", label: "Granted" },
  under_review:{ bg: "#e0f2fe", text: "#0369a1", border: "#7dd3fc", label: "Under Review" },
  not_due:     { bg: "#f1f5f9", text: "#64748b", border: "#cbd5e1", label: "Not Due" },

  // Surveillance
  scheduled:   { bg: "#e0f2fe", text: "#0369a1", border: "#7dd3fc", label: "Scheduled" },
  in_progress: { bg: "#fef9c3", text: "#854d0e", border: "#fde047", label: "In Progress" },
  completed:   { bg: "#dcfce7", text: "#166534", border: "#86efac", label: "Completed" },
  overdue:     { bg: "#fee2e2", text: "#991b1b", border: "#fca5a5", label: "Overdue" },
  cancelled:   { bg: "#f3f4f6", text: "#6b7280", border: "#d1d5db", label: "Cancelled" },

  // Renewals
  open:        { bg: "#dbeafe", text: "#1d4ed8", border: "#93c5fd", label: "Open" },
  submitted:   { bg: "#fef9c3", text: "#854d0e", border: "#fde047", label: "Submitted" },

  // Billing
  paid:        { bg: "#dcfce7", text: "#166534", border: "#86efac", label: "Paid" },
  unpaid:      { bg: "#fee2e2", text: "#991b1b", border: "#fca5a5", label: "Unpaid" },
  trialing:    { bg: "#e0f2fe", text: "#0369a1", border: "#7dd3fc", label: "Trialing" },

  // Course
  none:        { bg: "#f1f5f9", text: "#64748b", border: "#cbd5e1", label: "Not Accredited" },
};

// Default export for convenience
const tokens = {
  colors,
  spacing,
  spacingNum,
  typography,
  radius,
  shadows,
  animation,
  zIndex,
  layout,
  breakpoints,
  statusMap,
};

export default tokens;
